<script setup lang="ts">
import device from '@@/bits/device'
import { defineAsyncComponent } from '@@/bits/vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useAutoScrollElement } from '@@/pragmatic-drag-and-drop/useAutoScrollElement'
import type { Id } from '@@/types'
import SurfaceHeader from '@@/vuecomponents/SurfaceHeader.vue'
import {
  SURFACE_STREAM_POST_DRAGGABLE_TYPE,
  SURFACE_STREAM_SECTION_HEADER_DRAGGABLE_TYPE,
} from '@@/vuecomponents/SurfacePostsStreamSection.vue'
import { useDoubleClickToPost } from '@@/vuecomposables/surface_double_click_to_post'
import { useCollapseAroundTouchPointOnDragStart } from '@@/vuecomposables/useCollapseAroundTouchPointOnDragStart'
import { storeToRefs } from 'pinia'
import { ref } from 'vue'

const SurfaceAddSectionButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddSectionButton.vue'))
const SurfacePostsStream = defineAsyncComponent(() => import('@@/vuecomponents/SurfacePostsStream.vue'))

withDefaults(
  defineProps<{
    reactionIdsByPostId: Record<Id, Id[]>
    commentIdsByPostId: Record<Id, Id[]>
  }>(),
  {
    reactionIdsByPostId: () => ({}),
    commentIdsByPostId: () => ({}),
  },
)

defineEmits(['scroll'])

const { xHeader, backgroundLuminance, isReadOnly, isPostSizeStandard, isPostSizeWide, isFrozen } =
  storeToRefs(useSurfaceStore())
const { canIModerate } = storeToRefs(useSurfacePermissionsStore())
const { isGroupBySection, isGroupBySingleSelect } = storeToRefs(useSurfaceWishArrangementStore())
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const surfacePostsDragAndDropStore = useSurfacePostsDragAndDropStore()
const { isDraggingPostInNonManuallySortedWall, isAPostDragging } = storeToRefs(surfacePostsDragAndDropStore)
if (!device.mobile) useDoubleClickToPost()

const containerEl = ref<HTMLElement | null>(null)
useAutoScrollElement({
  scrollContainerElementRef: containerEl,
  type: [SURFACE_STREAM_SECTION_HEADER_DRAGGABLE_TYPE, SURFACE_STREAM_POST_DRAGGABLE_TYPE],
})

const postsContainerDragPadding = ref<{ paddingTop?: string; paddingBottom?: string }>({})
useCollapseAroundTouchPointOnDragStart({
  scrollContainerElementRef: containerEl,
  postContainerDragPaddingRef: postsContainerDragPadding,
  triggerRef: isAPostDragging,
})
</script>

<template>
  <div
    id="wall-container"
    ref="containerEl"
    :class="{
      'scrollbar-photo': backgroundLuminance === 'light',
      'scrollbar-photo-dark': backgroundLuminance === 'dark',
      'overflow-x-hidden': !isReadOnly,
    }"
    @scroll="$emit('scroll', $event)"
  >
    <SurfaceHeader v-if="xHeader" />

    <SurfacePostsStream
      class="mt-3 tablet-portrait:mt-4 pb-8"
      :reaction-ids-by-post-id="reactionIdsByPostId"
      :comment-ids-by-post-id="commentIdsByPostId"
      :style="postsContainerDragPadding"
    />
    <div
      v-if="
        (isGroupBySection || isGroupBySingleSelect) &&
        canIModerate &&
        !isReadOnly &&
        !isDraggingPostInNonManuallySortedWall &&
        !isFrozen
      "
      class="flex flex-col items-center pb-16"
    >
      <div
        v-if="!isDemoPadletPanelDesktop"
        :class="[
          'max-w-full',
          isPostSizeStandard && 'tablet-portrait:w-135',
          isPostSizeWide && 'tablet-portrait:w-187',
        ]"
      >
        <SurfaceAddSectionButton class="ms-4 tablet-portrait:ms-0" />
      </div>
    </div>
  </div>
</template>
